import { clearDurableTokens, flushDurableTokens, readDurableTokens, writeDurableTokens } from '@/utils/durableSession'

const ACCESS = 'access_token'
const REFRESH = 'refresh_token'
const ACCESS_MAX_AGE = 60 * 60

function lsGet(key: string): string | null {
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

function lsSet(key: string, value: string): void {
  try {
    localStorage.setItem(key, value)
  } catch {
    /* iOS private mode / quota */
  }
}

function lsRemove(key: string): void {
  try {
    localStorage.removeItem(key)
  } catch {
    /* ignore */
  }
}

function isHttps(): boolean {
  return window.location.protocol === 'https:'
}

function cookieSuffix(maxAge: number): string {
  return `;Path=/;SameSite=Lax;Max-Age=${maxAge};Secure`
}

function writeCookie(name: string, value: string, maxAge: number): void {
  if (!isHttps()) return
  const encoded = encodeURIComponent(value)
  if (encoded.length > 3500) return
  document.cookie = `${name}=${encoded}${cookieSuffix(maxAge)}`
}

function readCookie(name: string): string | null {
  const prefix = `${name}=`
  for (const part of document.cookie.split(';')) {
    const s = part.trim()
    if (s.startsWith(prefix)) return decodeURIComponent(s.slice(prefix.length))
  }
  return null
}

function deleteCookie(name: string): void {
  document.cookie = `${name}=;Path=/;Max-Age=0;SameSite=Lax`
  document.cookie = `${name}=;Path=/;Max-Age=0;SameSite=Lax;Secure`
}

export function getAccessToken(): string | null {
  return lsGet(ACCESS) || readCookie(ACCESS)
}

export function getRefreshToken(): string | null {
  return lsGet(REFRESH)
}

export function setTokens(access: string, refresh?: string | null): void {
  lsSet(ACCESS, access)
  writeCookie(ACCESS, access, ACCESS_MAX_AGE)
  deleteCookie(REFRESH)
  if (refresh) {
    lsSet(REFRESH, refresh)
  }
  writeDurableTokens(access, refresh || lsGet(REFRESH))
}

export function clearTokens(): void {
  lsRemove(ACCESS)
  lsRemove(REFRESH)
  deleteCookie(ACCESS)
  deleteCookie(REFRESH)
  clearDurableTokens()
}

export function hydrateTokensFromCookies(): void {
  if (!lsGet(ACCESS)) {
    const access = readCookie(ACCESS)
    if (access) lsSet(ACCESS, access)
  }
  deleteCookie(REFRESH)
}

export function hasStoredSession(): boolean {
  return Boolean(getAccessToken() || getRefreshToken())
}

export async function restoreDurableTokens(): Promise<void> {
  if (lsGet(ACCESS) && lsGet(REFRESH)) return
  const saved = await readDurableTokens()
  if (!saved) return
  if (!lsGet(ACCESS) && saved.access) lsSet(ACCESS, saved.access)
  if (!lsGet(REFRESH) && saved.refresh) lsSet(REFRESH, saved.refresh)
}

export { flushDurableTokens }
